import { Directive } from '@angular/core';
import { RcCompanyMetadata } from 'src/app/_types/lead.types';
import { getBusinessRcCompanyCoverageValidityLabel } from 'src/app/enums/business/business-rc-company-coverage-validity.enum';
import {
  getRcCompanyAdditionalCoverLabel,
  RcCompanyAdditionalCover,
} from 'src/app/enums/business/rc-company-additional-cover.enum';
import { getBooleanLabel } from 'src/app/helpers/utils';

import { BaseMetadataComponent } from '../../base-metadata.component';

type BusinessRcCompanyGenericCoverage = {
  withCoverage?: boolean;
  insuredSum?: number;
  deductible?: number;
};

@Directive()
export abstract class RcCompanyMetadataComponentAbstract extends BaseMetadataComponent<RcCompanyMetadata> {
  getBooleanLabel = getBooleanLabel;

  getRcCompanyAdditionalCoverLabel = getRcCompanyAdditionalCoverLabel;

  getBusinessRcCompanyCoverageValidityLabel =
    getBusinessRcCompanyCoverageValidityLabel;

  getAdditionalCoversText(covers?: RcCompanyAdditionalCover[]): string {
    if (!covers?.length) return '';
    return covers
      .map((cover) => getRcCompanyAdditionalCoverLabel(cover))
      .join(', ');
  }

  isGenericCoverageEmpty(coverage?: BusinessRcCompanyGenericCoverage): boolean {
    if (!coverage) return true;
    return (
      coverage.withCoverage === undefined &&
      coverage.insuredSum === undefined &&
      coverage.deductible === undefined
    );
  }

  isCompanyPersonalDamageCoverageEmpty(
    coverage?: RcCompanyMetadata['companyPersonalDamageCoverage']
  ): boolean {
    if (!coverage) return true;
    return (
      coverage.personalPropertyDamage === undefined &&
      coverage.insuredSum === undefined &&
      coverage.doubleWarranty === undefined &&
      coverage.dcFranchise === undefined &&
      coverage.dmFranchise === undefined &&
      coverage.financialLoss === undefined &&
      coverage.financialLossInsuredSum === undefined &&
      coverage.financialLossDeductible === undefined &&
      coverage.financialLossDataViolationCoverage === undefined
    );
  }

  hasBasicInfo(): boolean {
    return (
      this.metadata?.reimbursedSum !== undefined ||
      this.metadata?.insuredSum !== undefined ||
      this.metadata?.fees !== undefined ||
      this.metadata?.additionalCovers?.length > 0 ||
      this.metadata?.coverageValidityDuration !== undefined
    );
  }

  hasCurrentInsuranceInfo(): boolean {
    return (
      (this.metadata?.hasInsurance !== null &&
        this.metadata?.hasInsurance !== undefined) ||
      this.metadata?.currentInsurer !== undefined ||
      (this.metadata?.hasClaimsLastFiveYears !== null &&
        this.metadata?.hasClaimsLastFiveYears !== undefined)
    );
  }

  hasEnvironmentDamageCoverage(): boolean {
    return (
      !this.isGenericCoverageEmpty(
        this.metadata?.environmentDamageBasicCoverage
      ) ||
      !this.isGenericCoverageEmpty(
        this.metadata?.environmentDamageExtendedCoverage
      )
    );
  }

  hasDamagePreventionCoverage(): boolean {
    return (
      !this.isGenericCoverageEmpty(this.metadata?.damagePreventionCoverage) ||
      !this.isGenericCoverageEmpty(
        this.metadata?.misappropriationDamageCoverage
      ) ||
      !this.isGenericCoverageEmpty(
        this.metadata?.reputationRestorationCoverage
      ) ||
      !this.isGenericCoverageEmpty(this.metadata?.structureDamagesCoverage)
    );
  }

  hasAdditionalOptions(): boolean {
    return (
      (this.metadata?.importerInsured !== null &&
        this.metadata?.importerInsured !== undefined) ||
      (this.metadata?.intangibleAssets !== null &&
        this.metadata?.intangibleAssets !== undefined) ||
      (this.metadata?.eanBarcodes !== null &&
        this.metadata?.eanBarcodes !== undefined) ||
      (this.metadata?.ionizingRadiation !== null &&
        this.metadata?.ionizingRadiation !== undefined) ||
      (this.metadata?.cableTransportInstallationCombination !== null &&
        this.metadata?.cableTransportInstallationCombination !== undefined) ||
      (this.metadata?.firefightingExplosiveMaterialShipping !== null &&
        this.metadata?.firefightingExplosiveMaterialShipping !== undefined)
    );
  }

  hasBuildingItemsCoverage(): boolean {
    return (
      !this.isGenericCoverageEmpty(this.metadata?.buildingCoverage) ||
      !this.isGenericCoverageEmpty(this.metadata?.itemsCoverage)
    );
  }

  hasOtherCoverages(): boolean {
    return (
      !this.isGenericCoverageEmpty(this.metadata?.cisternCoverage) ||
      !this.isGenericCoverageEmpty(
        this.metadata?.installationAssemblyCoverage
      ) ||
      !this.isGenericCoverageEmpty(this.metadata?.workingCommunitiesCoverage) ||
      !this.isGenericCoverageEmpty(this.metadata?.workedObjectBasicCoverage) ||
      !this.isGenericCoverageEmpty(this.metadata?.workedObjectExtendedCoverage)
    );
  }
}
